import { DateTime } from "luxon";
import {
  differenceInYears,
  subMonths,
  subDays,
  isSameDay,
  differenceInHours,
  isEqual,
} from "date-fns";
import {
  startOfDay,
  addDays,
  endOfWeek,
  startOfWeek,
  differenceInCalendarYears,
  addMonths,
  endOfMonth,
  isBefore,
} from "date-fns";
import { UserModelType } from "../context/useAuthStore";

export default function useDateUtils() {
  // function getDaysInMonth(year, month) {
  //     return new Date(year, month, 0).getDate();
  // }

  // const capitalize = ([first,...rest]) => first.toUpperCase() + rest.join('').toLowerCase();
  const millisecondsInDay = 1000 * 60 * 60 * 24;

  const months = [
    "Januar",
    "Februar",
    "März",
    "April",
    "Mai",
    "Juni",
    "Juli",
    "August",
    "September",
    "Oktober",
    "November",
    "Dezember",
  ];

  const daysOfTheWeek = [
    "Montag",
    "Dienstag",
    "Mittwoch",
    "Donnerstag",
    "Freitag",
    "Samstag",
    "Sonntag",
  ];

  function isReportSectionDayOfWeek(reportSectionName: string): boolean {
    return daysOfTheWeek.some((e) => e.toUpperCase() === reportSectionName);
  }

  type DaysMap = {
    [key: string]: number;
  };

  function findDaysOfWeekBetween(
    startMillis: number,
    endMillis: number,
    dayOfWeekGerman: string
  ): Array<Date> {
    // Map German days of the week to JavaScript Date day indices
    const daysMap: DaysMap = {
      SONNTAG: 0,
      MONTAG: 1,
      DIENSTAG: 2,
      MITTWOCH: 3,
      DONNERSTAG: 4,
      FREITAG: 5,
      SAMSTAG: 6,
    };

    // Convert German day of the week to index
    const dayIndex = daysMap[dayOfWeekGerman.toUpperCase()];

    const dates = Array<Date>();
    let currentDate = new Date(startMillis);

    // Ensure the start date is before the end date
    const endDate = new Date(endMillis);
    if (currentDate > endDate) {
      console.error("Start date must be before end date.");
      return dates;
    }

    // Make sure the current date is set to the start of the day to ensure precise comparison
    currentDate.setHours(0, 0, 0, 0);

    while (currentDate <= endDate) {
      if (currentDate.getDay() === dayIndex) {
        // If the current day matches the specified day, add it to the results
        dates.push(new Date(currentDate));
        // Jump to the next week (7 days ahead)
        currentDate.setDate(currentDate.getDate() + 7);
      } else {
        // Move to the next day
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    return dates;
  }

  function reportSectionNameToDayOfWeekIndex(
    reportSectionName: string
  ): number {
    // ie. find Saturday with input SATURDAY || find Saturday with input BETRIEBSATURDAY
    const day = daysOfTheWeek.findIndex(
      (e) => reportSectionName.indexOf(e.toUpperCase()) !== -1
    );
    //console.log("day found -> ", day);
    return day;
  }

  function getTime(date: Date): number {
    //return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    return date.getTime() - date.getTimezoneOffset() * 60 * 1000;
  }

  function getRandomDayOfWeek() {
    return daysOfTheWeek[Math.floor(Math.random() * daysOfTheWeek.length)];
  }

  function getRandomDateBetweenTwoDates(start: number, end: number): Date {
    const diff = end - start;
    if (diff == 0) {
      const date = new Date(start);

      return resetDate(date);
    }
    //console.log("difference",diff);
    const date = new Date(+start + Math.random() * (diff + millisecondsInDay));

    return resetDate(date);
  }

  function resetDate(date: Date, returnNewDate = false) {
    const dateToReturn = returnNewDate
      ? new Date(date.getFullYear(), date.getMonth(), date.getDate())
      : date;

    dateToReturn.setMilliseconds(0);
    dateToReturn.setSeconds(0);
    dateToReturn.setMinutes(0);
    dateToReturn.setHours(0);
    return dateToReturn;
  }

  function getHoursFromMilliseconds(milliseconds: number): number {
    return parseFloat((milliseconds / 1000.0 / 60.0 / 60.0).toFixed(2));
  }

  type GetWorkDaysParams = {
    includeSunday: boolean;
    includeSaturday?: boolean;
  };
  function getWorkDays({
    includeSunday,
    includeSaturday = true,
  }: GetWorkDaysParams): Array<string> {
    return daysOfTheWeek.filter(
      (day) =>
        (day !== "Sonntag" || includeSunday) &&
        (day !== "Samstag" || includeSaturday)
    );
  }

  function getReportDates(
    start: Date,
    end: Date,
    reportType: number,
    mustBeSameMonth = true
  ) {
    const dates = [];
    let currentDate = startOfDay(new Date(start));

    if (reportType === 1) {
      end = addDays(new Date(end), 1); // Adjust end date to include the last day of the month for daily reports.
    }

    while (isBefore(currentDate, end)) {
      let startDate, endDate;

      switch (reportType) {
        case 1: // Daily
          startDate = currentDate;
          endDate = currentDate;
          currentDate = addDays(currentDate, 1);
          break;
        case 2: // Weekly
          startDate = getMondayOfCurrentWeek(currentDate);
          endDate = getSundayOfCurrentWeek(currentDate);
          currentDate = addDays(currentDate, 7);
          break;
        default: // Monthly
          startDate = startOfDay(currentDate);
          endDate = endOfMonth(currentDate);
          currentDate = addMonths(currentDate, 1);
          break;
      }

      if (mustBeSameMonth) {
        if (
          endDate.getMonth() === end.getMonth() ||
          startDate.getMonth() === start.getMonth()
        ) {
          dates.push({ startDate, endDate });
        }
      } else {
        dates.push({ startDate, endDate });
      }
    }
    return dates;
  }

  // function diffYears({ laterDate, earlierDate }) {
  //   return laterDate.getFullYear() - earlierDate.getFullYear();
  // }

  function getMonthLongName(d: Date) {
    return d.toLocaleString("de-DE", { month: "long" });
  }

  function getMondayOfCurrentWeek(d: Date) {
    var day = d.getDay();
    return new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate() + (day == 0 ? -6 : 1) - day
    );
  }
  function getSundayOfCurrentWeek(d: Date) {
    var day = d.getDay();
    return new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate() + (day == 0 ? 0 : 7) - day
    );
  }

  function getFirstDayOfCurrentMonth(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }
  function getLastDayOfCurrentMonth(d: Date) {
    //console.log("last day of month",new Date(d.getFullYear(), d.getMonth()+1, 0));
    return new Date(d.getFullYear(), d.getMonth() + 1, 0);
  }

  function isLeapYear(year: number) {
    return (year % 4 == 0 && year % 100 !== 0) || year % 400 == 0;
  }

  function getDaysInMonth(year: number, month: number) {
    return [
      31,
      isLeapYear(year) ? 29 : 28,
      31,
      30,
      31,
      30,
      31,
      31,
      30,
      31,
      30,
      31,
    ][month];
  }

  function padLeadingZeros(num: number, size: number) {
    var s = num + "";
    while (s.length < size) s = "0" + s;
    return s;
  }

  // function dateToYYYYmmDDString(date) {
  //   return (
  //     date.getFullYear() +
  //     "-" +
  //     padLeadingZeros(date.getMonth() + 1, 2) +
  //     "-" +
  //     padLeadingZeros(date.getDate(), 2)
  //   );
  // }

  //use dateformatter
  function dateToYYYYmmDDString(date: Date, separator = "-") {
    const formatter = DateTime.fromJSDate(date).toFormat(
      `yyyy${separator}MM${separator}dd`
    );
    return formatter;
  }

  // function dateToDDmmYYYYString(date, separator = ".") {
  //   return (
  //     padLeadingZeros(date.getDate(), 2) +
  //     separator +
  //     padLeadingZeros(date.getMonth() + 1, 2) +
  //     separator +
  //     date.getFullYear()
  //   );
  // }

  const dateToDDmmYYYYString = (date: Date, separator = ".") => {
    const formatter = DateTime.fromJSDate(date).toFormat(
      `dd${separator}MM${separator}yyyy`
    );
    return formatter;
  };

  function convertNumToTime(number: number) {
    // Check sign of given number
    var sign = number >= 0 ? 1 : -1;

    // Set positive value of number of sign negative
    number = number * sign;

    // Separate the int from the decimal part
    var hour = Math.floor(number);
    var decpart = number - hour;

    var min = 1.0 / 60.0;
    // Round to nearest minute
    decpart = min * Math.round(decpart / min);

    // console.log("decpart",decpart);
    var minute = Math.floor(decpart * 60.0);

    // console.log("minute",minute);

    if (minute == 60) {
      hour = hour + 1;
      minute = 0;
    }

    // Add padding if need

    // Add Sign in final result
    const signString = sign == 1 ? "" : "-";

    // Concate hours and minutes
    const time =
      signString + hour + "h&nbsp;" + padLeadingZeros(minute, 2) + "m";

    return time;
  }

  function getMinDateLong(
    reportType: number,
    user: UserModelType
  ): number | undefined {
    switch (reportType) {
      case 1:
        return getTime(startOfDay(new Date(user.startDate)));
      case 2:
        return getTime(getMondayOfCurrentWeek(new Date(user.startDate)));
      case 3:
        return getTime(getFirstDayOfCurrentMonth(new Date(user.startDate)));
    }
  }
  function getMaxDateLong(
    reportType: number,
    user: UserModelType
  ): number | undefined {
    const apprenticeshipEndDate = new Date(user.endDate);
    switch (reportType) {
      case 1:
        // apprenticeshipEndDate.setDate(apprenticeshipEndDate.getDate() + 1);
        return getTime(apprenticeshipEndDate); // include the last day aswell by making max the day after the last day
      case 2:
        return user.endDate;
      case 3:
        return user.endDate;
    }
  }
  //if using the first day of the month as the starting point, as is the norm with monthly reports, if it is the first report of the apprenticeship and the apprenticeship starts after the first day of that month, the month does not appear in the years accordion
  function fixStartOfTheMonthEdgeCase(
    reportType: number,
    user: UserModelType,
    startOfTheMonth: Date
  ): Date {
    switch (reportType) {
      case 1:
      case 2:
        return startOfTheMonth;
      case 3:
      default:
        return getTime(startOfTheMonth) < user.startDate
          ? new Date(user.startDate)
          : startOfTheMonth;
    }
  }

  return {
    millisecondsInDay,
    getHoursDifference: differenceInHours,
    getMinDateLong,
    getMaxDateLong,
    fixStartOfTheMonthEdgeCase,
    isSameDay,
    resetDate,
    convertNumToTime,
    getReportDates,
    getTime,
    getRandomDateBetweenTwoDates,
    getRandomDayOfWeek,
    reportSectionNameToDayOfWeekIndex,
    getWorkDays,
    diffYears: differenceInCalendarYears,
    getDaysInMonth,
    dateToYYYYmmDDString,
    dateToDDmmYYYYString,
    addMonths,
    subtractMonths: subMonths,
    getMondayOfCurrentWeek,
    getSundayOfCurrentWeek,
    getFirstDayOfCurrentMonth,
    getLastDayOfCurrentMonth,
    findDaysOfWeekBetween,
    isReportSectionDayOfWeek,
    months,
    getMonthLongName,
  };
}
